import { Link } from 'react-router-dom'
import { BookOpenCheck, Search } from 'lucide-react'
import type { Document, ReadinessSummary } from '@agentwiki/shared'
import { Button, Chip } from '../hybrid'
import { getDocumentKindInfo } from '../../lib/document-kind'
import type { HomeWorkQueueItem } from './home-work-queue-table'
import {
  WorkspaceKnowledgeDocRow,
  buildWorkspaceKnowledgeMetrics,
  formatNumber,
  verifiedCount,
} from './home-workspace-knowledge-primitives'
import { WorkspaceMetricGrid } from './home-workspace-knowledge-sections'
import { WorkspaceProjectTree } from './home-workspace-project-tree'
import { buildWorkspaceKnowledgeProjects } from './home-workspace-project-tree-data'

interface HomeWorkspaceKnowledgeProps {
  documents: Document[]
  isLoading?: boolean
  summary?: ReadinessSummary
  workQueue: HomeWorkQueueItem[]
  uploadStatusUnavailable?: boolean
}

export function HomeWorkspaceKnowledge({
  documents,
  isLoading = false,
  summary,
  workQueue,
  uploadStatusUnavailable = false,
}: HomeWorkspaceKnowledgeProps) {
  const verifiedDocs = documents.filter((doc) => getDocumentKindInfo(doc).kind === 'verified')
  const reviewDocs = documents.filter((doc) => {
    const kind = getDocumentKindInfo(doc).kind
    return kind === 'stale' || kind === 'conflict'
  })
  const verified = verifiedCount(summary, verifiedDocs.length)
  const reviewQueueCount = reviewDocs.length + workQueue.length
  const needsReview = uploadStatusUnavailable ? 0 : reviewQueueCount
  const sourceBacked =
    summary?.sources.documentsWithSource ?? documents.filter((doc) => doc.sourceProvenance).length
  const memoryItems = summary?.memory.total ?? 0
  const projects = buildWorkspaceKnowledgeProjects(documents)
  const visibleReviewDocs = reviewDocs.slice(0, 2)
  const visibleWorkQueue = workQueue.slice(0, Math.max(0, 2 - visibleReviewDocs.length))
  const visibleReviewCount = visibleReviewDocs.length + visibleWorkQueue.length
  const hiddenReviewCount = Math.max(0, reviewQueueCount - visibleReviewCount)

  const metrics = buildWorkspaceKnowledgeMetrics({
    verified,
    needsReview,
    uploadStatusUnavailable,
    sourceBacked,
    sourceCount: summary?.sources.sourceCount,
    memoryItems,
    edgeCount: summary?.graph.edgeCount,
  })

  return (
    <section className="aw-workspace-knowledge" aria-label="Workspace knowledge">
      <div className="aw-workspace-knowledge-head">
        <div>
          <span className="aw-label">Workspace knowledge</span>
          <h2>Browse workspace knowledge</h2>
          <p>
            Synced source-backed docs are ready to read; operational queues stay separate from the
            library.
          </p>
        </div>
        <div className="aw-workspace-knowledge-actions">
          <Button to="/doc?ownership=source-backed" variant="primary">
            <BookOpenCheck className="h-4 w-4" />
            Open Knowledge
          </Button>
          <Button to="/search">
            <Search className="h-4 w-4" />
            Search
          </Button>
        </div>
      </div>

      <WorkspaceMetricGrid metrics={metrics} />

      <div className="aw-workspace-knowledge-grid">
        <WorkspaceProjectTree projects={projects} isLoading={isLoading} />

        <section className="aw-workspace-panel">
          <div className="aw-workspace-panel-head">
            <div>
              <span className="aw-label">Operational queue</span>
              <h3>Stale, conflict, failed</h3>
            </div>
            <Chip tone={needsReview > 0 || uploadStatusUnavailable ? 'warn' : 'ok'}>
              {uploadStatusUnavailable ? 'unknown' : formatNumber(needsReview)}
            </Chip>
          </div>
          <div className="aw-workspace-review-list">
            {visibleReviewDocs.map((doc) => (
              <WorkspaceKnowledgeDocRow key={doc.id} doc={doc} />
            ))}
            {visibleWorkQueue.map((item) =>
              item.href ? (
                <Link key={item.id} to={item.href} className="aw-workspace-review-row">
                  <span>
                    <strong>{item.item}</strong>
                    <small>{item.source}</small>
                  </span>
                  <Chip tone={item.tone}>{item.signal}</Chip>
                </Link>
              ) : (
                <span key={item.id} className="aw-workspace-review-row">
                  <span>
                    <strong>{item.item}</strong>
                    <small>{item.source}</small>
                  </span>
                  <Chip tone={item.tone}>{item.signal}</Chip>
                </span>
              ),
            )}
            {hiddenReviewCount > 0 && (
              <span className="aw-workspace-review-more">
                {formatNumber(hiddenReviewCount)} additional operational{' '}
                {hiddenReviewCount === 1 ? 'warning' : 'warnings'}
              </span>
            )}
            {!isLoading && reviewQueueCount === 0 && (
              <div className="aw-workspace-empty">No operational warnings.</div>
            )}
          </div>
        </section>
      </div>
    </section>
  )
}
